import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, FileText, ClipboardList, Pill as PillIcon, Shield, TrendingUp, FolderArchive, ShieldCheck, UserCheck } from "lucide-react";
import { Page, Card, Pill, PrimaryButton } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";
import { ClientPortalPanel } from "../components/ClientPortalPanel";

export default function ClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [privacy, setPrivacy] = useState(null);

  useEffect(() => {
    api.get(`/clients/${id}`).then(({ data }) => setData(data)).catch(() => toast.error("Failed to load client"));
    api.get(`/privacy/client/${id}`).then(({ data }) => setPrivacy(data));
  }, [id]);

  const togglePrivacy = async (key) => {
    const next = !privacy[key];
    const newPriv = { ...privacy, [key]: next };
    setPrivacy(newPriv);
    try {
      await api.put(`/privacy/client/${id}`, { [key]: next });
      toast.success("Privacy updated");
    } catch {
      toast.error("Save failed");
      setPrivacy(privacy);
    }
  };

  if (!data) return <Page title="Client"><div className="mf-skeleton h-48"></div></Page>;

  const { client, notes, plans, medications, safety_plans, outcomes, documents } = data;

  const PRIVACY_TOGGLES = [
    { key: "ai_notes", label: "AI Note Generation" },
    { key: "session_recording", label: "Session Recording" },
    { key: "ai_transcription", label: "AI Transcription" },
    { key: "ai_session_analysis", label: "AI Session Analysis" },
    { key: "wearable_data", label: "Wearable Data Integration" },
    { key: "predictive_crisis_monitoring", label: "Predictive Crisis Monitoring" },
    { key: "research_participation", label: "Research Participation" },
    { key: "sensitive_record_flag", label: "Sensitive Record (Restricted Mode)" },
  ];

  const tabs = [
    { key: "overview", label: "Overview", icon: null },
    { key: "notes", label: `Notes (${notes?.length || 0})`, icon: FileText },
    { key: "plans", label: `Treatment Plans (${plans?.length || 0})`, icon: ClipboardList },
    { key: "meds", label: `Medications (${medications?.length || 0})`, icon: PillIcon },
    { key: "safety", label: `Safety (${safety_plans?.length || 0})`, icon: Shield },
    { key: "outcomes", label: `Outcomes (${outcomes?.length || 0})`, icon: TrendingUp },
    { key: "documents", label: `Documents (${documents?.length || 0})`, icon: FolderArchive },
    { key: "portal", label: "Portal & Reminders", icon: UserCheck },
  ];

  return (
    <Page
      title={`${client.first_name} ${client.last_name}`}
      subtitle={client.diagnosis || "No diagnosis on file"}
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => setPrivacyOpen(!privacyOpen)} className="px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2" style={{ background: "var(--mf-muted)", color: "var(--mf-primary)" }} data-testid="open-privacy-panel">
            <ShieldCheck className="w-4 h-4" /> Privacy
          </button>
          <Link to="/clients" className="flex items-center gap-1 text-sm" style={{ color: "var(--mf-primary)" }} data-testid="back-to-clients">
            <ChevronLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      }
    >
      {privacyOpen && privacy && (
        <Card delay={0} className="mb-5" testid="privacy-panel">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-5 h-5" style={{ color: "var(--mf-primary)" }} />
            <h3 className="font-serif text-xl">Per-Client Privacy</h3>
          </div>
          <div className="space-y-2">
            {PRIVACY_TOGGLES.map(t => (
              <div key={t.key} className="flex items-center justify-between p-3 rounded-xl" style={{ background: "var(--mf-muted)" }}>
                <div className="text-sm font-medium">{t.label}</div>
                <button onClick={() => togglePrivacy(t.key)} className="w-12 h-7 rounded-full relative transition-colors shrink-0" style={{ background: privacy[t.key] ? "var(--mf-primary)" : "rgba(0,0,0,0.1)" }} data-testid={`priv-${t.key}`}>
                  <span className="absolute top-1 w-5 h-5 rounded-full bg-white transition-transform" style={{ left: privacy[t.key] ? "calc(100% - 1.5rem)" : "0.25rem" }}></span>
                </button>
              </div>
            ))}
          </div>
          <div className="text-xs mt-3" style={{ color: "var(--mf-text-muted)" }}>Some toggles require signed client consent forms before activation.</div>
        </Card>
      )}
      {/* Client header card */}
      <Card delay={0.05} className="mb-5" testid="client-header">
        <div className="flex items-start gap-5">
          <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-serif text-2xl shrink-0" style={{ background: "var(--mf-primary)" }}>
            {client.first_name?.[0]}{client.last_name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h2 className="font-serif text-2xl">{client.first_name} {client.last_name}</h2>
              <Pill variant={client.risk_level === "high" ? "danger" : client.risk_level === "moderate" ? "warn" : "success"}>{client.risk_level} risk</Pill>
              {client.language !== "en" && <Pill variant="primary">{client.language.toUpperCase()}</Pill>}
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-y-2 text-sm" style={{ color: "var(--mf-text-muted)" }}>
              {client.email && <div><span className="text-xs uppercase tracking-wider">Email</span><div style={{ color: "var(--mf-text)" }}>{client.email}</div></div>}
              {client.phone && <div><span className="text-xs uppercase tracking-wider">Phone</span><div style={{ color: "var(--mf-text)" }}>{client.phone}</div></div>}
              {client.dob && <div><span className="text-xs uppercase tracking-wider">DOB</span><div style={{ color: "var(--mf-text)" }}>{client.dob}</div></div>}
              {client.pronouns && <div><span className="text-xs uppercase tracking-wider">Pronouns</span><div style={{ color: "var(--mf-text)" }}>{client.pronouns}</div></div>}
            </div>
            {client.presenting_concerns && (
              <div className="mt-3 text-sm" style={{ color: "var(--mf-text)" }}>{client.presenting_concerns}</div>
            )}
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${tab === t.key ? "" : "hover:bg-black/5"}`}
            style={tab === t.key ? { background: "var(--mf-primary)", color: "#FAFAF8" } : { color: "var(--mf-text-muted)" }}
            data-testid={`client-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "portal" && <ClientPortalPanel client={client} />}
      {tab === "overview" && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card testid="overview-next-actions">
            <h3 className="font-serif text-xl mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <Link to={`/notes?client=${client.id}`} className="flex items-center justify-between p-3 rounded-xl hover:bg-black/5" data-testid="quick-write-note">
                <span className="text-sm">Write a progress note</span>
                <FileText className="w-4 h-4" style={{ color: "var(--mf-primary)" }} />
              </Link>
              <Link to={`/treatment-plans?client=${client.id}`} className="flex items-center justify-between p-3 rounded-xl hover:bg-black/5" data-testid="quick-create-plan">
                <span className="text-sm">Create treatment plan</span>
                <ClipboardList className="w-4 h-4" style={{ color: "var(--mf-primary)" }} />
              </Link>
              <Link to={`/safety-plans?client=${client.id}`} className="flex items-center justify-between p-3 rounded-xl hover:bg-black/5" data-testid="quick-safety-plan">
                <span className="text-sm">Build safety plan</span>
                <Shield className="w-4 h-4" style={{ color: "var(--mf-primary)" }} />
              </Link>
            </div>
          </Card>
          <Card testid="overview-recent-notes">
            <h3 className="font-serif text-xl mb-4">Recent Notes</h3>
            {notes.length === 0 ? (
              <div className="text-sm py-4" style={{ color: "var(--mf-text-muted)" }}>No notes yet.</div>
            ) : (
              <div className="space-y-2">
                {notes.slice(0, 3).map((n) => (
                  <div key={n.id} className="p-3 rounded-xl" style={{ background: "var(--mf-muted)" }}>
                    <div className="flex items-center justify-between mb-1">
                      <Pill variant="primary">{n.format}</Pill>
                      <span className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{new Date(n.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="text-xs line-clamp-2" style={{ color: "var(--mf-text)" }}>{n.final_content?.slice(0, 200)}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === "notes" && (
        <Card>
          {notes.length === 0 ? (
            <div className="text-sm py-6 text-center" style={{ color: "var(--mf-text-muted)" }}>
              No notes yet. <Link to={`/notes?client=${client.id}`} className="underline" style={{ color: "var(--mf-primary)" }}>Write one now</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {notes.map((n) => (
                <div key={n.id} className="p-4 rounded-xl" style={{ background: "var(--mf-muted)" }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Pill variant="primary">{n.format}</Pill>
                      <Pill>{n.template}</Pill>
                    </div>
                    <span className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{new Date(n.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap mf-prose">{n.final_content}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "plans" && (
        <Card>
          {plans.length === 0 ? (
            <div className="text-sm py-6 text-center" style={{ color: "var(--mf-text-muted)" }}>
              No treatment plans yet. <Link to={`/treatment-plans?client=${client.id}`} className="underline" style={{ color: "var(--mf-primary)" }}>Generate one</Link>
            </div>
          ) : (
            <div className="space-y-3">
              {plans.map((p) => (
                <div key={p.id} className="p-4 rounded-xl" style={{ background: "var(--mf-muted)" }}>
                  <div className="flex items-center justify-between mb-2">
                    <Pill variant="primary">v{p.version}</Pill>
                    <span className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{new Date(p.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap mf-prose">{p.content}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "meds" && (
        <Card>
          {medications.length === 0 ? <div className="text-sm text-center py-6" style={{ color: "var(--mf-text-muted)" }}>No medications recorded.</div> : (
            <div className="space-y-2">
              {medications.map((m) => (
                <div key={m.id} className="p-3 rounded-xl flex items-center justify-between" style={{ background: "var(--mf-muted)" }}>
                  <div>
                    <div className="font-medium text-sm">{m.drug} · {m.dosage}</div>
                    <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Prescribed by {m.prescriber} · started {m.start_date}</div>
                  </div>
                  <Pill variant="success">{m.status}</Pill>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "safety" && (
        <Card>
          {safety_plans.length === 0 ? <div className="text-sm text-center py-6" style={{ color: "var(--mf-text-muted)" }}>No safety plan on file.</div> : (
            <div className="space-y-3">
              {safety_plans.map((sp) => (
                <div key={sp.id} className="p-4 rounded-xl" style={{ background: "var(--mf-muted)" }}>
                  <div className="flex items-center justify-between mb-2">
                    <Pill variant={sp.signed ? "success" : "warn"}>{sp.signed ? "Signed" : "Unsigned"}</Pill>
                    <span className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Review due {new Date(sp.review_due).toLocaleDateString()}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap mf-prose">{sp.ai_suggestions}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "outcomes" && (
        <Card>
          {outcomes.length === 0 ? <div className="text-sm text-center py-6" style={{ color: "var(--mf-text-muted)" }}>No outcome measures yet.</div> : (
            <div className="space-y-2">
              {outcomes.map((o) => (
                <div key={o.id} className="p-3 rounded-xl flex items-center justify-between" style={{ background: "var(--mf-muted)" }}>
                  <div>
                    <div className="font-medium text-sm">{o.measure_type}</div>
                    <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{new Date(o.date).toLocaleDateString()}</div>
                  </div>
                  <div className="font-serif text-2xl" style={{ color: "var(--mf-primary)" }}>{o.score}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === "documents" && (
        <Card testid="client-documents">
          {(!documents || documents.length === 0) ? <div className="text-sm text-center py-6" style={{ color: "var(--mf-text-muted)" }}>No documents. <Link to="/documents" className="underline" style={{ color: "var(--mf-primary)" }}>Upload or scan</Link></div> : (
            <div className="space-y-2">
              {documents.map((d) => (
                <div key={d.id} className="p-3 rounded-xl flex items-center justify-between" style={{ background: "var(--mf-muted)" }}>
                  <div>
                    <div className="font-medium text-sm">{d.filename}</div>
                    <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{d.doc_type} · {Math.round(d.size_bytes/1024)} KB</div>
                  </div>
                  <Pill variant="primary">{d.source}</Pill>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </Page>
  );
}
